import { ArticleAuthorAndDate } from "app/articles/components/ArticleAuthorAndDate"
import { categoryForestToTree } from "app/categories/categoryForestToTree"
import { CategoryPageLinkTree } from "app/categories/components/CategoryPageLinkTree"
import { colContainer, colNarrow, colWide } from "app/common/components/Containers"
import { SideHeading, SideLinks } from "app/common/components/SideLinks"
import { StructuredText } from "app/common/components/StructuredText"
import { selectUrl } from "app/common/core/types"
import { RouteMgr } from "app/common/routes"
import { optionalRender } from "app/common/ui/optionalRender"
import { variantsToSrcSet } from "app/common/utilities/html"
import { StructuredTextEntities } from "app/common/utilities/structuredText"
import { ArticleVM } from "app/models/article"
import { Link } from "blitz"
import { LocaleD } from "db"
import { array, option } from "fp-ts"
import { pipe } from "fp-ts/function"
import useTranslation from "next-translate/useTranslation"
import { FaShapes } from "react-icons/fa"
import { HiClock, HiHeart, HiTrendingUp } from "react-icons/hi"
import { tw } from "twind"

interface ImageVariant {
  url: string
  width: number
}

interface PageDetails {
  content: object
  id: number
  leadImage: {
    height: number
    url: string
    variants: Array<ImageVariant>
    width: number
  }
  number: number
  title: string
}

export interface ArticleDetailsProps {
  article: ArticleVM
  entities: StructuredTextEntities
  locale: LocaleD
  paging: {
    currentPage: PageDetails
    nextPage: option.Option<Omit<PageDetails, "content">>
    prevPage: option.Option<Omit<PageDetails, "content">>
  }
  popularArticles: ReadonlyArray<ArticleVM>
  recentArticles: ReadonlyArray<ArticleVM>
  trendingArticles: ReadonlyArray<ArticleVM>
}

export const ArticleDetails = ({
  article: { authors, categories, image, publishedAt, slug },
  entities,
  locale,
  paging: { currentPage, nextPage, prevPage },
  popularArticles,
  recentArticles,
  trendingArticles,
}: ArticleDetailsProps) => {
  const { t } = useTranslation("articles")

  return (
    <div className={tw(colContainer)}>
      <div className={tw(colWide)}>
        <div className={tw(`flex(& col) gap-12`)} key={currentPage.title}>
          <div className={tw(`relative`, `overflow-hidden`)}>
            <img
              alt={image.alternateText}
              height={currentPage.leadImage.height}
              sizes={"1000px"}
              src={currentPage.leadImage.url}
              srcSet={variantsToSrcSet(currentPage.leadImage.variants)}
              width={currentPage.leadImage.width}
            />
            <div
              className={tw(
                `flex(& col sm:row) gap(1 sm:2) justify-between items(start sm:center)`,
                `absolute bottom-0 left-0 right-0`,
                `bg(white opacity-60)`,
                `px-8 py-4`
              )}
            >
              <h1 className={tw(`text(3xl gray-800) font-semibold`)}>{currentPage.title}</h1>{" "}
              <ArticleAuthorAndDate authors={authors} date={publishedAt} locale={locale} />
            </div>
          </div>

          <StructuredText data={currentPage.content} entities={entities} />
        </div>

        {[prevPage, nextPage].some(option.isSome) && (
          <div className={tw(`flex(& col lg:row) justify-between`, `py-2`)}>
            {pipe(
              prevPage,
              optionalRender((pp) => (
                <Link
                  href={RouteMgr.ArticlePage({
                    number: pp.number,
                    slug,
                  })}
                  key={pp.number}
                >
                  <a
                    className={tw(`flex(& 1) gap-10 items-center`, `border-2`, `text(base sm:xl)`)}
                    rel="prev"
                  >
                    <img
                      alt={image.alternateText}
                      className={tw(`w(32 sm:44)`)}
                      height={image.height}
                      sizes={"200px"}
                      src={pipe(
                        pp.leadImage.variants,
                        array.findFirst((v) => v.url.includes("100x")),
                        option.map(selectUrl),
                        option.toUndefined
                      )}
                      srcSet={variantsToSrcSet(pp.leadImage.variants)}
                      width={image.width}
                    />
                    <div className={tw(`flex(& col) gap-1 pr-3`)}>
                      <div className={tw(`font-bold`)}>Back</div>
                      <div>{pp.title}</div>
                    </div>
                  </a>
                </Link>
              ))
            )}

            {pipe(
              nextPage,
              optionalRender((np) => (
                <Link
                  href={RouteMgr.ArticlePage({
                    number: np.number,
                    slug,
                  })}
                  key={np.number}
                >
                  <a
                    className={tw(
                      `border-2`,
                      `flex(& 1 row-reverse) gap-10 items-center`,
                      `text(base sm:xl)`,
                      option.isSome(prevPage) && `border(t-0 lg:(l-0 t-2))`
                    )}
                    rel="next"
                  >
                    <img
                      alt={image.alternateText}
                      className={tw(`w(32 sm:44)`)}
                      height={image.height}
                      sizes={"200px"}
                      src={np.leadImage.url}
                      srcSet={variantsToSrcSet(np.leadImage.variants)}
                      width={image.width}
                    />
                    <div className={tw(`flex(& col) gap-1`, `text-right`)}>
                      <div className={tw(`font-bold`)}>Next</div>
                      <div>{np.title}</div>
                    </div>
                  </a>
                </Link>
              ))
            )}
          </div>
        )}
      </div>

      <aside className={tw(colNarrow)}>
        <SideLinks
          Icon={HiTrendingUp}
          links={pipe(
            trendingArticles,
            array.map((article) => ({
              image: article.image,
              key: article.id,
              routeUrl: RouteMgr.ArticlePage({ slug: article.slug }),
              text: article.title,
            }))
          )}
          title={t("trending")}
        />
        <SideLinks
          Icon={HiHeart}
          links={pipe(
            popularArticles,
            array.map((article) => ({
              image: article.image,
              key: article.id,
              routeUrl: RouteMgr.ArticlePage({ slug: article.slug }),
              text: article.title,
            }))
          )}
          title={t("all-time-favourites")}
        />
        <SideLinks
          Icon={HiClock}
          links={pipe(
            recentArticles,
            array.map((article) => ({
              image: article.image,
              key: article.id,
              routeUrl: RouteMgr.ArticlePage({ slug: article.slug }),
              text: article.title,
            }))
          )}
          title={t("recent")}
        />
        <div className={tw(`mb-2`, `flex(& col) gap-4`)}>
          <SideHeading Icon={FaShapes} title={t("categories")} />
          <CategoryPageLinkTree categories={categoryForestToTree(categories)} />
        </div>
      </aside>
    </div>
  )
}
